<template>
  <div class="mt-8">
    <!-- proposal.contractId -->
    <div class="mb-4 text-2xl font-semibold">
      <span class="text-gray-500">#</span>
      <span>{{ proposal.contractId }}</span>
    </div>

    <form @submit.stop.prevent="form.handleSubmit()">
      <div class="grid gap-4 sm:grid-cols-4">
        <!-- Quote for -->
        <div class="col-span-2">
          <form.Field
            name="customerId"
            :validators="{
              onBlur: yup.string().required('Quote for is required')
            }"
          >
            <template #default="{ field }">
              <CustomerAutoComplete
                :model-value="field.state.value"
                :name="field.name"
                label="Quote for"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @update:model-value="(value) => field.handleChange(value)"
              />
            </template>
          </form.Field>
        </div>

        <!-- Issued on -->
        <div>
          <form.Field
            name="contractDate"
            :validators="{
              onBlur: yup.string().required('Issued on is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Issued on"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <!--  Valid until -->
        <div>
          <form.Field
            name="contractValidDate"
            :validators="{
              onBlur: yup.string().required('Valid until is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Valid until"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <!-- original contract items -->
      <div class="mt-8">
        <div
          v-for="(lineItems, category) in getProposalItems"
          :key="category"
          class="mb-3"
        >
          <div class="mb-2 pb-2 border-b border-slate-200">
            <span class="font-semibold text-xl">{{ category }}</span>
          </div>

          <div
            v-for="(lineItem) in lineItems"
            :key="lineItem.id"
          >
            <div>
              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-4 flex items-start">
                  <div
                    v-if="!lineItem.isCreditLineItem"
                    class="cursor-pointer mr-2 mt-4"
                    @click="toggleOptional(lineItem.id)"
                  >
                    <!-- optional badge -->
                    <BaseBadge
                      v-if="lineItem.optional"
                      variant="warning"
                    >
                      Optional
                    </BaseBadge>
                    <BaseBadge
                      v-else
                      variant="success"
                    >
                      Required
                    </BaseBadge>
                  </div>

                  <BaseCollapse :bg-color="'bg-withe'">
                    <template #header>
                      <div class="flex items-center">
                        <span class="font-bold">{{ lineItem.title }}</span>
                      </div>
                    </template>

                    <div class="text-justify" v-html="lineItem.scopeOfWork" />

                    <!-- attachments -->
                    <div class="flex flex-wrap gap-4 mt-4">
                      <div
                        v-for="file in lineItem.attachments"
                        :key="file"
                        class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
                      >
                        <img
                          v-if="isImage(file)"
                          :src="file"
                          class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
                        >
                        <div
                          v-else
                          class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
                        >
                          <div class="text-dark font-weight-bold">
                            {{ fileTypeToString(file) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </BaseCollapse>
                </div>

                <!-- make optional dropdown -->

                <!-- totals -->
                <div class="flex flex-col gap-1 mt-4 text-sm">
                  <p class="text-primary-400">{{ currency(lineItem.cost) }}</p>
                  <p class="text-primary-400">{{ decimalFormat(lineItem.margin, 2) }}% margin</p>
                  <p v-if="lineItem.quantity > 1">{{ currency(lineItem.unitPrice) }}</p>
                  <p v-if="lineItem.quantity > 1">Qty: x {{ lineItem.quantity }}</p>
                  <p>{{ currency(lineItem.totalPrice) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr class="border-b-slate-200 border-2" />

      <!-- contract total -->
      <div class="mt-8 ml-auto min-w-[30%] max-w-fit">
        <div class="bg-slate-100 p-4 rounded-lg gap-4 text-right">
          <div class="grid grid-cols-2">
            <span class="font-semibold">Contract Value:</span> <span>{{ currency(proposalTotalPrice) }}</span>
          </div>
        </div>
      </div>

      <!-- Terms and Conditions -->
      <div class="mt-8">
        <span class="text-lg font-bold block mb-2">Terms and Conditions</span>

        <form.Field
          name="termsConditions"
          :validators="{
            onChange: yup.string().transform((value) => value.replace(/<[^>]*>/g, '')).required('Terms and Conditions is required')
          }"
        >
          <template #default="{ field }">
            <Editor
              v-model="(field.state.value as string)"
              :name="field.name"
              class="min-h-28"
              @update:model-value="(value) => field.handleChange(value)"
            />
            <!-- errors -->
            <div v-if="field.state.meta.errors" class="text-red-500 text-sm mt-1">
              {{ field.state.meta.errors[0] }}
            </div>
          </template>
        </form.Field>
      </div>


      <!-- submit and cancel buttons -->
      <hr class="mt-6">
      <form.Subscribe>
        <template #default="{ canSubmit, isSubmitting }">
          <div class="grid items-center grid-cols-2 gap-4 mt-4">
            <!-- last auto-saving indicator -->
            <div class="text-sm text-gray-500">
              <div
                v-if="props.autoSave"
                class="flex items-center gap-2"
              >
                <span>Last auto-saved at:</span>
                <span v-if="!autoSaving" class="font-bold">
                  {{ lastAutoSave ? lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}
                </span>
                <span v-if="autoSaving" class="i-heroicons-ellipsis-horizontal animate-pulse font-bold size-6 inline-block" />
              </div>

              <div v-else class="text-gray-500 font-bold">
                Auto save is disabled
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 flex-1">
              <BaseButton
                variant="base"
                text="Cancel"
                :to="{ name: 'ShowProposal', params: { id: proposal.id } }"
                :disabled="isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              />

              <BaseButton
                variant="primary"
                text="Save"
                type="submit"
                :disabled="!canSubmit || isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
              />
            </div>
          </div>
        </template>
      </form.Subscribe>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import { filter, contains, compact } from 'underscore'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import Editor from '@/components/TextEditor.vue'
import CustomerAutoComplete from '@/components/base/CustomerAutoComplete.vue'
import { Project } from '@/models/project'
import { Customer } from '@/models/customer'
import { Budget } from '@/models/budget'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { computed, onBeforeUnmount, PropType, ref, watch } from 'vue'
import { currency, decimalFormat, fileTypeToString, isImage, sortLineItemsByCategoryName, toNumber } from '@/utils'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { useNotification } from '@kyvg/vue3-notification'
import BudgetService from '@/services/BudgetService'
import ProposalService from '@/services/ProposalService'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'
import { useQueryClient } from '@tanstack/vue-query'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  customer: {
    type: Object as PropType<Customer>,
    required: true
  },
  budget: {
    type: Object as PropType<Budget>,
    required: true
  },
  model: {
    type: Object as PropType<Proposal>,
    required: true
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  autoSaveInterval: {
    type: Number,
    default: 10
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const proposal = ref({ ...props.model })
const budgetToUpdate = ref(JSON.parse(JSON.stringify(props.budget)) as Budget)
const lineItemsToUpdate = computed(() => budgetToUpdate.value.lineItems)
const { notify } = useNotification()
const queryClient = useQueryClient()
const hasUnsavedChanges = ref(true)

const form = useForm({
  defaultValues: proposal.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    value.contractTotal = contractTotal.value
    await props.saveFunction({ proposal: value, budget: budgetToUpdate.value })
    hasUnsavedChanges.value = false
  },
})

const getProposalItems = computed(() => {
  let items = []

  if (proposal.value.status === PROPOSAL_STATUS.DRAFT || proposal.value.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) {
    items = compact(budgetToUpdate.value.lineItems)
  } else {
    items = filter(budgetToUpdate.value.lineItems, (item) => contains(proposal.value.lineItems, item.id))
  }

  return sortLineItemsByCategoryName(items)
})

const proposalTotalPrice = computed(() => {
  return props.budget.lineItems
    .filter(item => proposal.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const toggleOptional = (lineItemId: string) => {
  const index = lineItemsToUpdate.value.findIndex(item => item.id === lineItemId)
  const item = lineItemsToUpdate.value[index]


  lineItemsToUpdate.value.splice(index, 1, {
    ...item,
    optional: !item.optional
  })

  budgetToUpdate.value.lineItems = lineItemsToUpdate.value
}

const contractTotal = computed(() => {
  return budgetToUpdate.value.lineItems
    .filter(item => proposal.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})


let autoSaveTimeout: ReturnType<typeof setInterval>
const lastAutoSave = ref<Date | null>(null)
const autoSaving = ref(false)
const budgetService = new BudgetService()
const proposalService = new ProposalService()

watch(() => proposal.value.status, (newStatus) => {
  if (newStatus == PROPOSAL_STATUS.DRAFT) {

    if (!props.autoSave) {
      return
    }

    clearInterval(autoSaveTimeout)
    autoSaveTimeout = setInterval(async () => {
      const formState = form.useStore((state) => state)
      if (!formState.value.canSubmit) {
        return
      }

      autoSaving.value = true
      lastAutoSave.value = null
      try {
        await budgetService.updateBudget(budgetToUpdate.value, [])
        const proposalData = {
          ...form.useStore((state) => state.values).value,
          contractTotal: contractTotal.value,
          lineItems: proposal.value.lineItems,
        }
        await proposalService.update(proposal.value.projectId, proposalData)
        lastAutoSave.value = new Date()
      } catch (error) {
        console.error('Auto save error:', error)
        notify({
          type: 'error',
          title: 'Auto save error',
          text: error.message || 'An error occurred while auto saving the proposal'
        })
      } finally {
        autoSaving.value = false
      }
    }, props.autoSaveInterval * 1000)
  } else {
    clearInterval(autoSaveTimeout)
  }
}, { immediate: true })


onBeforeUnmount(() => {
  clearInterval(autoSaveTimeout)
  if (lastAutoSave.value) {
    const proposalData = {
      ...form.useStore((state) => state.values).value,
      contractTotal: contractTotal.value,
      lineItems: proposal.value.lineItems,
    }
    queryClient.setQueryData(['proposal', proposal.value.id], proposalData)
  }
})

// prevent user from navigating away without saving
useUnsavedChangesWarning(hasUnsavedChanges)


</script>
